import bbox from '@turf/bbox';
import centroid from '@turf/centroid';
import {AxiosResponse} from 'axios';
import {Feature, FeatureCollection, Polygon} from 'geojson';
import {AllowedUsersModel} from 'library/src/authentication/models/AllowedUsersModel';
import {getErrorMessage, logger} from 'library/src/core/services/LogService';
import {FieldForm} from 'library/src/fields/models/AddField';
import FieldCreation from 'library/src/fields/models/FieldCreation';
import {PixagriField} from 'library/src/fields/models/PixagriField';
import {FieldIrrigationService} from 'library/src/fields/models/WagoField';
import PixagriSubscription from 'library/src/pixagriApi/subscriptions/models/PixagriSubscription';
import ProcessingType from 'library/src/processing/models/ProcessingType';
import {AuthState} from 'modules/auth/models/AuthState';
import Campaign from 'modules/campaigns/models/Campaign';
import CampaignsState from 'modules/campaigns/models/CampaignsState';
import {getCurrentCampaignBis} from 'modules/campaigns/services/CampaignsService';
import nitrogenActions from 'modules/nitrogen/actions/NitrogenActions';
import {subscriptionActions} from 'modules/subscriptions/actions/SubscriptionsActions';
import {HydroStressLevel} from 'modules/waterBalance/models/HydroStressLevel';
import {getHydroStressLevel} from 'modules/waterBalance/services/WaterBalanceService';
import {PutEffect, all, call, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {fetchError, fetchStart, fetchSuccess, redirectTo} from 'shared/actions/Common';
import {initialUrl} from 'shared/configs/AppConst';
import {ServiceId} from 'shared/models/RouterParam';
import log from 'shared/services/LogService';
import {AppState} from 'shared/store/index';
import {appIntl} from 'shared/utils/IntlGlobalProvider';
import wellknown from 'wellknown';
import api, {pixagriApiUrl, wagoNestApiUrl} from '../../../shared/services/ApiConfig';
import {actions as rotationActions} from '../../rotations/actions/RotationsActions';
import actions, {
  ADD_NEW_FIELD,
  AddNewFieldAction,
  DELETE_FIELD,
  DeleteFieldAction,
  IMPORT_FIELDS,
  ImportFieldsAction,
  LOAD_FIELDS_SUMMARY,
  LoadFieldsSummaryAction,
  MODIFY_FIELD,
  MODIFY_USER,
  ModifyFieldAction,
  ModifyUserAction,
  ON_SELECT_FILTERS,
  OnSelectFiltersAction,
  RELOAD_FIELDS_BACKGROUND,
  ReloadFieldsBackgroundAction,
  SET_SELECT_PRODUCER,
  SetSelectProducerAction,
  UPDATE_FIELDS_BULK,
  UPDATE_VISIO_CROP_FIELD,
  UpdateFieldsBulkAction,
  UpdateVisioCropFieldAction,
} from '../actions/FieldsActions';
import {CURRENTLY_SUBSCRIBE_ERROR} from '../configs/FieldConfigs';
import {getServiceOverviewFullRoute} from '../configs/FieldsRoutePaths';
import getFieldSubscribeStatusForFilter from '../helpers/getFieldSubscribeStatusForFilter';
import {FieldFilterPropertyKey} from '../models/FieldFilters';
import FieldsState from '../models/FieldsState';
import {getSortedProducers} from '../services/FieldsService';

function getProducerFieldsRequest(fieldsId?: number[]) {
  const fieldsSummaryUrl = `${wagoNestApiUrl}/fields/wago`;
  if (fieldsId) {
    const query = fieldsId.map((id) => `fields=${id}`).join('&');
    return api.get(`${fieldsSummaryUrl}?${query}`);
  }
  return api.get(fieldsSummaryUrl);
}

function modifyUserRequest(user: AllowedUsersModel): Promise<AxiosResponse<any>> {
  const fieldUrl = `${wagoNestApiUrl}/users`;
  return api.post(fieldUrl, user);
}

function addToCrops(crops: string[], cropName: string) {
  if (!crops.includes(cropName)) {
    crops.push(cropName);
  }
}

function updateVisioCropFieldRequest(field: FieldCreation) {
  const fieldUrl = `${wagoNestApiUrl}/fields`;
  return api.post(fieldUrl, field);
}
function updateFieldsBulkRequest(fields: FieldCreation[], service: ProcessingType) {
  const url = `${wagoNestApiUrl}/fields/${service}`;
  return api.post(url, fields);
}

function* updateVisioCropField(action: UpdateVisioCropFieldAction) {
  try {
    yield put(fetchStart('updateVisioCropField'));
    yield call(updateVisioCropFieldRequest, action.payload.field);
    yield call(reloadFieldsSummaryAfterDelay, 2);
    yield put(fetchSuccess('updateVisioCropField'));
  } catch (error) {
    yield put(fetchError('updateVisioCropField', error as any));
  }
}
function* reloadFieldsOnBackground(action: ReloadFieldsBackgroundAction) {
  try {
    const res: AxiosResponse = yield call(getProducerFieldsRequest, action.payload.fieldIds);
    let fieldsSummary = res.data as PixagriField[];
    const {farmersList}: FieldsState = yield select((state: AppState) => state.fields);
    const fieldsMap: {[key: string]: PixagriField} = {};

    if (fieldsSummary) {
      fieldsSummary.forEach((field) => {
        // Add Uid to field
        const selectedFarmerList = farmersList.find((farmer) => farmer.farmer_id === field.farmer_id);
        if (selectedFarmerList && selectedFarmerList.uid) {
          field.farmer_uid = selectedFarmerList.uid;
        }

        // Get different crop from service irrigation and compute hydroStress level
        if (field.irrigation.length) {
          field.irrigation.forEach((rotation: FieldIrrigationService) => {
            // Compute hydrostress level

            if (rotation.RAW && rotation.TAW) {
              rotation.hydro_stress_level = getHydroStressLevel(
                rotation.RAWc?.most_recent_value!,
                field.field_thresholds,
              );
            } else {
              rotation.hydro_stress_level = HydroStressLevel.UNDEFINED;
            }
          });
        }
        field.geojson_geometry = wellknown.parse(field.geometry) as Polygon;

        fieldsMap[field.field_id] = field;
      });
      yield put(actions.reloadFieldsBackgroundSuccess(fieldsMap));
    }
  } catch (error) {
    logger.error('Error while reloading fields background', error);
  }
}

//Sends api request to Redux store
function* loadFieldsSummary(action: LoadFieldsSummaryAction) {
  // retrieve intl only now to be sure correctly initialized
  const intl = appIntl();

  try {
    yield put(fetchStart('loadFieldsSummary'));
    const res: AxiosResponse = yield call(getProducerFieldsRequest);
    let fieldsSummary = res.data as PixagriField[];

    if (!fieldsSummary) {
      fieldsSummary = [];
    }

    const fieldsCount = fieldsSummary.length;

    // Get CampaignState
    const {data}: CampaignsState = yield select((state: AppState) => state.campaigns);
    const {token, user}: AuthState = yield select((state: AppState) => state.auth);

    //const {thresholds}: CarbonState = yield select((state: AppState) => state.carbon);

    const {selectedFieldId, selectedProducer}: FieldsState = yield select((state: AppState) => state.fields);
    const campaigns: Campaign[] = Object.values(data);
    let currentCampaignId: number | null = null;
    if (campaigns) {
      currentCampaignId = getCurrentCampaignBis(campaigns);
    }

    let selectedCampaignId = action.payload.selectedCampaignId;

    const fieldsMap: {[key: string]: PixagriField} = {};

    if (!token || !user) {
      throw new Error('No token');
    }
    const farmerList: FieldsState['farmersList'] = yield call(getSortedProducers, token, user);

    // Compute data for fields and get farmers
    const farmers: string[] = [];
    const crops: string[] = [];
    const fieldsSubscriptions: {[key: number]: PixagriSubscription} = {};

    let featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    fieldsSummary.forEach((field) => {
      // Add to collection Farmers
      if (!farmers.includes(field.farmer)) {
        farmers.push(field.farmer);
      }

      // Add Uid to field
      const selectedFarmerList = farmerList.find((farmer) => farmer.farmer_id === field.farmer_id);
      if (selectedFarmerList && selectedFarmerList.uid) {
        field.farmer_uid = selectedFarmerList.uid;
      }

      // Get different crop from service irrigation and compute hydroStress level
      if (field.irrigation.length) {
        field.irrigation.forEach((rotation: FieldIrrigationService) => {
          // Push crops in map if not exist
          rotation.field_id = field.field_id;
          //rotation.crop_type = getCropFromNameMatch(rotation.crop_name);

          const cropName = rotation.crop_name;
          addToCrops(crops, cropName);

          // Compute hydrostress level

          if (rotation.RAW && rotation.TAW) {
            rotation.hydro_stress_level = getHydroStressLevel(
              rotation.RAWc?.most_recent_value!,
              field.field_thresholds,
            );
          } else {
            rotation.hydro_stress_level = HydroStressLevel.UNDEFINED;
          }

          // Get different crop for  service vegetation
          if (field.vegetation && field.vegetation.length) {
            field.vegetation.forEach((veg) => {
              // Push crops in map if not exist
              const cropName = veg.crop_name;
              addToCrops(crops, cropName);
            });
          }
        });
      }

      // Compute Bbox
      field.geojson_geometry = wellknown.parse(field.geometry) as Polygon;
      if (field.geojson_geometry) {
        const feature: Feature = {
          type: 'Feature',
          id: field.field_id,
          geometry: field.geojson_geometry,
          properties: {},
        };

        // Add it to the list of features in order to compute bbox
        featureCollection.features.push(feature);
        field.bbox = bbox(feature);
        field.centroid = centroid(feature).geometry.coordinates;

        if (field.vegetation.length) {
          field.vegetation.forEach((veg) => {
            let ndvi_bbox: number[] = [];
            if (veg.fcover && veg.fcover.pic_bbox) {
              try {
                ndvi_bbox = veg.fcover.pic_bbox as unknown as number[];
              } catch (error: any) {
                log.error(`Error while parsing ndvi_pic_bbox for field ${field.field_id}`);
              }
            }

            veg.ndvi_coordinates = [
              [ndvi_bbox[0], ndvi_bbox[3]],
              [ndvi_bbox[2], ndvi_bbox[3]],
              [ndvi_bbox[2], ndvi_bbox[1]],
              [ndvi_bbox[0], ndvi_bbox[1]],
            ];
          });
        }
      }

      const serviceSubscriptions = Object.keys(field.subscriptions);
      serviceSubscriptions.forEach((key: string) => {
        const subs = field.subscriptions[key as keyof PixagriField['subscriptions']];
        if (subs && subs.length) {
          subs.forEach((sub) => {
            if (!fieldsSubscriptions[sub.sub_id as number]) {
              fieldsSubscriptions[sub.sub_id as number] = sub;
            }
          });
        }
      });

      fieldsMap[field.field_id] = field;
    });
    yield put(subscriptionActions.loadSubscriptionsSuccess({}, fieldsSubscriptions));

    const allFeaturesBbox = bbox(featureCollection);
    const fieldsState: FieldsState = {
      count: fieldsCount,
      //data: fieldsMap,
      fieldsList: fieldsMap,
      campaigns: campaigns,
      crops: crops,
      farmersList: farmerList,
      farmers: farmers,
      selectedService: null,
      selectedFieldId: selectedFieldId,
      selectedCampaign: selectedCampaignId,
      zoomToSelected: false,
      mapPosition: {
        latitude: 0,
        longitude: 0,
        zoom: 3,
      },
      bbox: allFeaturesBbox,
      mapDisplayNdvi: false,
      selectedProducer: selectedProducer ? selectedProducer : null,
      fieldUpdateErrors: null,
    };

    yield put(actions.loadFieldsSummarySuccess(fieldsState));
    yield put(fetchSuccess('loadFieldsSummary'));
  } catch (error: any) {
    log.error(`Error loading fields: ${error.message}`, error);
    yield put(fetchError('loadFieldsSummary', intl.formatMessage({id: 'fields.errors.errorLoading'})));
  }
}

/**
 *
 * @param action Load the water balance data when a field is selected
 */
/* Function temporary disable, waiting for implementation choice
function* selectField(action: OnSelectFieldAction) {
  if (action.payload.selectedFieldId) {

    if(action.payload.serviceId && action.payload.serviceId === ServiceId.IRRIGATION) {

      const { data, selectedCampaign }: FieldsState = yield select((state: AppState) => state.fields)

      if(selectedCampaign && data) {

        const fieldSelected = data[selectedCampaign][action.payload.selectedFieldId]
        const rotationSelected = fieldSelected.irrigation.find(rotation => rotation.campaign_id === selectedCampaign);

        if(rotationSelected) {

          yield put(
            waterBalanceActions.loadWaterBalance(
              action.payload.selectedFieldId,
              rotationSelected.id_rotation,
              ),
              );
            }
          }
        }
  }
}
*/
function* selectFilters(action: OnSelectFiltersAction) {
  try {
    yield put(fetchStart('selectFilters'));

    const {selectedFilters} = action.payload;
    log.debug('Applying filter', selectedFilters);

    const {fieldsList, selectedCampaign, selectedService, selectedFieldId, selectedProducer}: FieldsState =
      yield select((state: AppState) => state.fields);

    if (!fieldsList) {
      log.info('No fields to filter');
      yield put(actions.onSelectFiltersSuccess(selectedFilters || {}, [], [], action.payload.zoomToSelected));
      yield put(fetchSuccess('selectFilters'));
      throw new Error('No fields to filter');
    }

    // Map pour retrouver rapidement les champs par id
    const fieldsMap: {[key: string]: PixagriField} = fieldsList;
    /*Object.values(fieldsList).forEach((field) => {
      fieldsMap[field.field_id] = field;
    });*/

    if (!selectedFilters) {
      log.info('No filters provided');
      const allIds = Object.keys(fieldsList).map((key) => Number(key));

      let featureCollection: FeatureCollection = {
        type: 'FeatureCollection',
        features: [],
      };

      allIds.forEach((fieldId) => {
        const item = fieldsMap[fieldId];
        if (item && item.geojson_geometry) {
          const feature: Feature = {
            type: 'Feature',
            id: item.field_id,
            geometry: item.geojson_geometry,
            properties: {},
          };
          featureCollection.features.push(feature);
        }
      });

      const allBbox = featureCollection.features.length ? bbox(featureCollection) : [];

      yield put(actions.onSelectFiltersSuccess({}, allIds, allBbox, action.payload.zoomToSelected));
      yield put(fetchSuccess('selectFilters'));
      return;
    }

    const multiPropsFilter = (): PixagriField[] => {
      const filtersKeys = Object.keys(selectedFilters) as FieldFilterPropertyKey[];
      const filtered: PixagriField[] = [];

      Object.values(fieldsList).forEach((field) => {
        let fieldMatch = true;
        for (let i = 0; i < filtersKeys.length && fieldMatch; i++) {
          const keyName = filtersKeys[i];
          const values = selectedFilters[keyName];

          if (!values || !values.length) {
            continue;
          }

          if (keyName === FieldFilterPropertyKey.CROP_NAME) {
            const irrigationFound = field.irrigation.find(
              (rotation) => values.includes(rotation.crop_name) && rotation.campaign_id === selectedCampaign,
            );
            const vegetationFound = field.vegetation.find(
              (vegetation) => values.includes(vegetation.crop_name) && vegetation.campaign_id === selectedCampaign,
            );

            if (!irrigationFound && !vegetationFound) {
              fieldMatch = false;
            }
          }

          if (keyName === FieldFilterPropertyKey.FARMER) {
            if (!values.includes(field.farmer)) {
              fieldMatch = false;
            }
          }

          if (keyName === FieldFilterPropertyKey.SUBSCRIBE_STATUS) {
            const status = getFieldSubscribeStatusForFilter(field, selectedService, selectedCampaign);
            if (!values.includes(status)) {
              fieldMatch = false;
            }
          }
        }
        if (fieldMatch && selectedProducer) {
          if (field.farmer_id !== selectedProducer.farmer_id) {
            fieldMatch = false;
          }
        }

        if (fieldMatch) {
          filtered.push(field);
        }
      });

      return filtered;
    };
    const filteredElements = multiPropsFilter();
    const itemdAdded = filteredElements.map((element) => element.field_id);
    log.debug(`Filtered fields count: ${itemdAdded?.length}`);
    let featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    itemdAdded.forEach((fieldId) => {
      const item = fieldsMap[fieldId];
      if (item && item.geojson_geometry) {
        const feature: Feature = {
          type: 'Feature',
          id: item.field_id,
          geometry: item.geojson_geometry,
          properties: {},
        };
        featureCollection.features.push(feature);
      }
    });

    const filteredFeaturesBbox = featureCollection.features.length ? bbox(featureCollection) : [];

    yield put(
      actions.onSelectFiltersSuccess(selectedFilters, itemdAdded, filteredFeaturesBbox, action.payload.zoomToSelected),
    );

    if (selectedFieldId && !itemdAdded.includes(selectedFieldId)) {
      log.debug(`Currently selected field is not part of the selection after applying filter, redirecting`);
      if (selectedCampaign && selectedService) {
        yield put(redirectTo(getServiceOverviewFullRoute(selectedCampaign, selectedService)));
      } else {
        yield put(redirectTo(initialUrl));
      }
    }

    yield put(fetchSuccess('selectFilters'));
  } catch (error: any) {
    log.error(`Error while applying filters`, error);
    yield put(fetchError('selectFilters', error.message));
  }
}

function addNewFieldRequest(newField: FieldForm) {
  const fieldUrl = `${pixagriApiUrl}/fields`;
  return api.post(fieldUrl, newField);
}

function* importFields(action: ImportFieldsAction) {
  // retrieve intl only now to be sure correctly initialized
  const intl = appIntl();
  try {
    if (
      action.payload.featureCollection &&
      action.payload.featureCollection.features &&
      action.payload.featureCollection.features.length > 0
    ) {
      yield put(fetchStart('importFields'));
      let fieldTemplate: FieldCreation = action.payload.template;
      const {selectedService}: FieldsState = yield select((state: AppState) => state.fields);
      const creationRequests: PutEffect<any>[] = [];
      const today = new Date();
      if (selectedService) {
        action.payload.featureCollection.features.forEach((feature, index) => {
          // Make a copy to be sure
          const newField = {...fieldTemplate};
          // Extract the wkt from the geojson
          // Convert to wkt
          newField.geometry = wellknown.stringify(feature.geometry as wellknown.GeoJSONPolygon);

          // Build the name
          if (feature.properties?.name || feature.properties?.Name) {
            newField.field_name = feature.properties.name || feature.properties?.Name;
          } else {
            newField.field_name = intl.formatMessage(
              {id: 'fields.import.default_name_template'},
              {number: index + 1, importDate: today},
            );
          }

          log.debug(newField);
          creationRequests.push(put(actions.addNewField(newField, selectedService)));
          //creationRequests.push(call(addNewFieldRequest, newField));
        });
      }
      yield all(creationRequests);
      yield put(fetchSuccess('importFields'));

      yield call(reloadFieldsSummaryAfterDelay, 2);
    } else {
      log.warn('Missing some information for fields import');
    }
  } catch (error: any) {
    log.error(`Error while importing fields`, error);
    const message = intl.formatMessage({
      id: 'fields.import.fields_creation_error',
    });
    yield put(fetchError('importFields', message));
  }
}

function* addNewField(action: AddNewFieldAction) {
  try {
    yield put(fetchStart('addNewField'));
    let {field, service} = action.payload;
    const {cover, rotation, station_ids, ...otherProps} = field;
    const newField = {
      ...otherProps,
      connected_station_ids: station_ids ? station_ids : [],
    };
    const res = yield call(addNewFieldRequest, newField as FieldForm);
    const createdField = res.data as {field_id: number; campaign_id: number};

    if (createdField && createdField.field_id) {
      log.info(`Field created with id ${createdField.field_id} for campaign ${createdField.campaign_id}`);

      switch (service) {
        case ServiceId.IRRIGATION:
          if (rotation && rotation.sowing_date && rotation.sys_irrigation_id && rotation.crop_id) {
            yield put(rotationActions.addNewRotation(createdField.field_id, rotation));
            break;
          } else {
            log.error(`Cannot post rotation: no data to post`);
            break;
          }
        case ServiceId.VEGETATION:
          if (rotation && rotation.sowing_date && rotation.sys_irrigation_id && rotation.crop_id) {
            yield put(rotationActions.addNewRotation(createdField.field_id, rotation));
            break;
          } else {
            log.error(`Cannot post rotation: no data to post`);
            break;
          }
        case ServiceId.NITROGEN:
          if (cover) {
            yield put(nitrogenActions.addCover(createdField.field_id, cover));
            break;
          } else {
            log.error(`Cannot post cover: no data to post`);
            break;
          }
        default:
          break;
      }
    }
    const {fieldsList, farmersList}: FieldsState = yield select((state: AppState) => state.fields);
    const farmerId = Object.values(fieldsList).find((farmer) => farmer.field_id === field.field_id)?.farmer_id;
    if (farmerId) {
      const farmer = farmersList.find((farmer) => farmer.farmer_id === farmerId);
      if (farmer) {
        const newFarmer: AllowedUsersModel = {
          ...farmer,
          irrigation_cost: field.irrigation_cost ? +field.irrigation_cost : null,
        };
        yield put(actions.modifyUser(newFarmer));
      }
    }

    yield put(fetchSuccess('addNewField'));
    yield call(reloadFieldsSummaryAfterDelay, 2);
  } catch (error: any) {
    yield put(fetchError('addNewField', error.message));
  }
}

export function* reloadFieldsSummaryAfterDelay(
  delayMultiplier: number = 1,
  fieldsId?: number[],
  backgroundReload: boolean = false,
) {
  // Wait for some times

  //yield delay(timeoutDuration * delayMultiplier);

  // Reload fields
  const {user}: AuthState = yield select((state: AppState) => state.auth);
  const {selectedCampaign}: FieldsState = yield select((state: AppState) => state.fields);
  if (backgroundReload) {
    yield put(actions.reloadFieldsBackground(fieldsId));
  } else if (user) {
    const action: LoadFieldsSummaryAction = {
      type: LOAD_FIELDS_SUMMARY,
      payload: {
        selectedFieldId: null,
        zoomToSelected: true,
        selectedCampaignId: selectedCampaign,
        user: user,
      },
    };
    yield call(loadFieldsSummary, action);
  } else {
    throw new Error(
      appIntl().formatMessage({
        id: 'field.modify.reload.error',
      }),
    );
  }
}

function modifyFieldRequest(field: FieldForm): Promise<AxiosResponse<any>> {
  const fieldUrl = `${pixagriApiUrl}/fields/${field.field_id}`;
  const {field_id, ...values} = field;
  return api.put(fieldUrl, values);
}

function* modifyField(action: ModifyFieldAction) {
  try {
    yield put(fetchStart('modifyField'));
    const {field, service} = action.payload;
    const {cover, rotation, station_ids, ...otherProps} = field;
    const newField = {
      ...otherProps,
      connected_station_ids: station_ids ? station_ids : [],
    };

    if (!otherProps.field_id) {
      throw Error(`Cannot update field with id ${otherProps.field_id}`);
    }
    const {field_id} = newField;
    //TODO: Fix type
    const res: AxiosResponse<any> = yield call(modifyFieldRequest as any, newField);

    if (res.status < 300) {
      log.info(`field ${field_id} updated`);

      switch (service) {
        case ServiceId.VEGETATION:
        case ServiceId.IRRIGATION:
          if (rotation) {
            yield put(rotationActions.modifyRotation(field_id!, rotation));
            break;
          } else {
            log.error(`Cannot update rotation: no data to post`);
            break;
          }
        case ServiceId.NITROGEN:
          if (cover) {
            yield put(nitrogenActions.updateCover(field_id!, cover));
            break;
          } else {
            log.error(`Cannot update cover: no data to post`);
            break;
          }
        default:
          break;
      }
    } else {
      throw Error(`Field not updated, Api response with status ${res.status}`);
    }
    const {fieldsList, farmersList}: FieldsState = yield select((state: AppState) => state.fields);
    const farmerId = Object.values(fieldsList).find((farmer) => farmer.field_id === field.field_id)?.farmer_id;
    if (farmerId) {
      const farmer = farmersList.find((farmer) => farmer.farmer_id === farmerId);
      if (farmer) {
        const newFarmer: AllowedUsersModel = {
          ...farmer,
          irrigation_cost: field.irrigation_cost ? +field.irrigation_cost : null,
        };
        yield put(actions.modifyUser(newFarmer));
      }
    }
    yield put(fetchSuccess('modifyField'));
    yield call(reloadFieldsSummaryAfterDelay);
  } catch (error: any) {
    yield put(fetchError('modifyField', error.message));
  }
}

function deleteFieldRequest(selectedField: number) {
  const fieldUrl = `${pixagriApiUrl}/fields/${selectedField}`;
  return api.delete(fieldUrl);
}

function* deleteField(action: DeleteFieldAction) {
  try {
    yield put(fetchStart('deleteField'));
    yield call(deleteFieldRequest, action.payload);
    yield put(fetchSuccess('deleteField'));

    // Redirect to campaign
    const {selectedCampaign, selectedService}: FieldsState = yield select((state: AppState) => state.fields);

    if (!selectedCampaign) {
      throw new Error('No campaign selected');
    }

    if (!selectedService) {
      throw new Error('No service selected');
    }

    yield put(redirectTo(getServiceOverviewFullRoute(selectedCampaign, selectedService)));

    yield call(reloadFieldsSummaryAfterDelay);
  } catch (error: any) {
    const {data} = error.response;
    if (data && data.error && data.error === CURRENTLY_SUBSCRIBE_ERROR) {
      yield put(
        fetchError(
          'deleteField',
          appIntl().formatMessage({
            id: 'field.delete.error.cuurently_subscribe',
          }),
        ),
      );
    } else {
      yield put(fetchError('deleteField', error.message));
    }
  }
}

function* setSelectProducer(action: SetSelectProducerAction) {
  try {
    const {user}: AuthState = yield select((state: AppState) => state.auth);
    if (!user) {
      throw new Error('User not found');
    }
    yield put(fetchStart('setSelectProducer'));
    //yield put(actions.loadFieldsSummary(farmer_id, null, true, null, user));
    yield put(fetchSuccess('setSelectProducer'));
  } catch (error: any) {
    yield put(fetchError('setSelectProducer', error.message));
  }
}

function* modifyUser(action: ModifyUserAction) {
  try {
    yield put(fetchStart('modifyUser'));
    const res: AxiosResponse<AllowedUsersModel> = yield call(modifyUserRequest, action.payload);
    yield put(actions.modifyUserSuccess(res.data));
    yield put(fetchSuccess('modifyUser'));
  } catch (error: any) {
    yield put(fetchError('modifyUser', error.message));
  }
}

function* updateFieldsBulk(action: UpdateFieldsBulkAction) {
  try {
    yield put(fetchStart('updateFieldsBulk'));
    // Call api to update fields in bulk
    const res: AxiosResponse<{
      fields: PixagriField[];
      errors: {fieldCreation: FieldCreation; error: any}[];
    }> = yield call(updateFieldsBulkRequest, action.payload.fields, action.payload.service);
    //yield call(updateFieldsBulkRequest, fields);
    yield put(actions.updateFieldsBulkSuccess(res.data.fields, res.data.errors));

    if (action.payload.farmers) {
      yield all(action.payload.farmers.map((farmer) => put(actions.modifyUser(farmer))));
    }
    yield put(fetchSuccess('updateFieldsBulk'));
  } catch (error: any) {
    const errorMessage = getErrorMessage(error);
    yield put(fetchError('updateFieldsBulk', errorMessage));
  }
}
export function* fieldsSagas() {
  yield all([
    takeEvery(LOAD_FIELDS_SUMMARY, loadFieldsSummary),
    // takeEvery(ON_SELECT_FIELD, selectField),
    takeEvery(ON_SELECT_FILTERS, selectFilters),
    takeEvery(ADD_NEW_FIELD, addNewField),
    takeEvery(IMPORT_FIELDS, importFields),
    takeEvery(MODIFY_FIELD, modifyField),
    takeEvery(DELETE_FIELD, deleteField),
    takeEvery(SET_SELECT_PRODUCER, setSelectProducer),
    takeEvery(MODIFY_USER, modifyUser),
    takeEvery(UPDATE_FIELDS_BULK, updateFieldsBulk),
    takeEvery(UPDATE_VISIO_CROP_FIELD, updateVisioCropField),
    takeLatest(RELOAD_FIELDS_BACKGROUND, reloadFieldsOnBackground),
  ]);
}
